import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ExternalLink, Heart, MapPin, Quote, ShoppingBag, Sparkles } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import DemoNotice from "@/components/demo/DemoNotice";
import ProfileModuleShell from "@/components/athlete/ProfileModuleShell";
import FollowAthleteDialog from "@/components/support/FollowAthleteDialog";
import ShareStoryDialog from "@/components/support/ShareStoryDialog";
import WorkWithAthleteSection from "@/components/support/WorkWithAthleteSection";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SITE_ORIGIN } from "@/lib/share";
import { DEMO_PROFILE_PATH, demoAthlete as a, demoRelated } from "@/data/demoAthlete";

const label = "text-xs font-semibold uppercase tracking-wider";
const pill =
  "inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";

/**
 * Public, fully populated profile demonstration. Static fictional content;
 * every action reuses the real UI in demo mode and writes nothing.
 */
const AthleteProfileDemo = () => {
  const [followOpen, setFollowOpen] = useState(false);
  const [following, setFollowing] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [bag, setBag] = useState<string[]>([]);

  const scrollToWork = () => document.getElementById("work-with-her")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Navbar />

      <DemoNotice />

      {/* 1. HERO */}
      <header className="container grid gap-8 py-8 md:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] md:items-center md:py-14">
        <div className="relative overflow-hidden rounded-3xl shadow-card aspect-[4/5] md:order-2">
          <img src={a.image} alt={`${a.name}, sample athlete, dribbling on a soccer pitch`} width={1280} height={1600}
            className="h-full w-full object-cover object-[50%_25%]" />
        </div>
        <div className="min-w-0 md:order-1">
          <p className={`${label} text-primary-strong`}>#{a.jersey} · {a.position}</p>
          <h1 className="mt-2 font-display text-5xl uppercase leading-[0.95] text-secondary md:text-7xl break-words">{a.name}</h1>
          <p className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
            <span>{a.sport}</span><span aria-hidden>·</span><span>{a.position}</span><span aria-hidden>·</span>
            <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" aria-hidden />{a.location}</span>
            <span aria-hidden>·</span><span>{a.level}</span>
          </p>
          <p className="serif mt-5 text-2xl italic leading-snug text-secondary md:text-3xl">{a.tagline}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <button type="button" onClick={() => setFollowOpen(true)} className={`${pill} bg-primary text-primary-foreground hover:bg-primary-strong`}>
              {following ? <Check className="h-4 w-4" aria-hidden /> : <Heart className="h-4 w-4" aria-hidden />}
              {following ? `Following ${a.firstName}` : `Follow ${a.firstName}`}
            </button>
            <ShareStoryDialog name="SIIS Athlete Profile Demo" slug={a.slug} tagline="Avery Morgan is fictional. All content is illustrative; demo actions save nothing." url={`${SITE_ORIGIN}${DEMO_PROFILE_PATH}`} />
            <button type="button" onClick={scrollToWork} className={`${pill} border border-border bg-card text-foreground hover:bg-muted`}>
              Work With Her
            </button>
          </div>
        </div>
      </header>

      <main className="container flex flex-col gap-10 pb-16 md:gap-14">
        {/* 2. KNOW HER */}
        <section aria-labelledby="know-her" className="flex flex-col gap-6">
          <h2 id="know-her" className={`${label} text-muted-foreground`}>Know Her</h2>
          <blockquote className="rounded-3xl bg-secondary p-6 text-secondary-foreground md:p-10">
            <Quote className="h-6 w-6 text-primary-on-navy" aria-hidden />
            <p className="serif mt-3 text-3xl italic leading-tight md:text-5xl">{a.statement}</p>
            <footer className={`${label} mt-4 text-primary-on-navy`}>— {a.name}</footer>
          </blockquote>
          <div className="max-w-3xl space-y-4 text-base leading-relaxed">
            {a.story.map((p) => <p key={p.slice(0, 20)}>{p}</p>)}
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {a.cards.map((c) => (
              <div key={c.title} className="rounded-2xl bg-card p-5 shadow-card">
                <p className={`${label} text-primary-strong`}>{c.title}</p>
                <p className="mt-2 text-sm leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 3. FOLLOW HER / MY CORNER */}
        <ProfileModuleShell active title="Follow Her · My Corner">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <p className="max-w-xl text-sm leading-relaxed text-muted-foreground">
              Fans who follow {a.firstName} see her published updates in My Corner. Following never signs anyone up for SIIS Weekly.
            </p>
            <button type="button" onClick={() => setFollowOpen(true)} className={`${pill} bg-primary text-primary-foreground hover:bg-primary-strong`}>
              <Heart className="h-4 w-4" aria-hidden /> {following ? "Update preferences" : `Follow ${a.firstName}`}
            </button>
          </div>
          <ul className="mt-5 grid gap-3 md:grid-cols-3">
            {a.updates.map((u) => (
              <li key={u.title} className="rounded-xl border border-border bg-background p-4">
                <p className="text-xs text-muted-foreground">{u.date} · Sample update</p>
                <p className="mt-1 font-semibold">{u.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{u.body}</p>
              </li>
            ))}
          </ul>
        </ProfileModuleShell>

        {/* 4. BACK HER GAME */}
        <section aria-labelledby="back-her" className="flex flex-col gap-4">
          <h2 id="back-her" className={`${label} text-muted-foreground`}>Back Her Game</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl bg-card p-5 shadow-card">
              <h3 className="font-display text-2xl uppercase">Back Her Directly</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Illustrative support only. Avery is fictional; no support page opens and no payment is taken.
              </p>
              <button type="button" onClick={() => setSupportOpen(true)} className={`${pill} mt-4 bg-secondary text-secondary-foreground hover:bg-primary hover:text-primary-foreground`}>
                Back Her Directly <ExternalLink className="h-4 w-4" aria-hidden />
              </button>
            </div>
            <div className="rounded-2xl bg-card p-5 shadow-card">
              <h3 className="font-display text-2xl uppercase">Her Collection</h3>
              <ul className="mt-3 divide-y divide-border">
                {a.collection.map((item) => {
                  const inBag = bag.includes(item.name);
                  return (
                    <li key={item.name} className="flex items-center justify-between gap-3 py-3">
                      <div className="min-w-0">
                        <p className="font-semibold">{item.name}</p>
                        <p className="text-xs text-muted-foreground">{item.price} · {item.note}</p>
                      </div>
                      <button type="button" aria-pressed={inBag}
                        onClick={() => { setBag(inBag ? bag.filter((n) => n !== item.name) : [...bag, item.name]); if (!inBag) toast.success("Demo profile: added to the sample bag. No checkout."); }}
                        className={`${pill} shrink-0 px-4 border border-border ${inBag ? "bg-muted" : "bg-background hover:bg-muted"}`}>
                        {inBag ? <Check className="h-4 w-4" aria-hidden /> : <ShoppingBag className="h-4 w-4" aria-hidden />}
                        {inBag ? "In bag" : "Add"}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-2 text-xs text-muted-foreground">Sample bag: {bag.length} item{bag.length === 1 ? "" : "s"}. Checkout is disabled in the demo.</p>
            </div>
          </div>
          <div className="rounded-2xl border border-primary/20 bg-primary/10 p-5">
            <p className={`${label} text-primary-strong`}>Her cause</p>
            <p className="mt-1 font-semibold">{a.cause.name}</p>
            <p className="mt-1 text-sm text-muted-foreground">{a.cause.blurb}</p>
          </div>
        </section>

        {/* 5. WORK WITH HER — the real inquiry form in demo mode */}
        <section id="work-with-her" aria-labelledby="work-with-her-label" className="scroll-mt-24 flex flex-col gap-4">
          <h2 id="work-with-her-label" className={`${label} text-muted-foreground`}>Work With Her</h2>
          <WorkWithAthleteSection athleteId={a.id} athleteName={a.name} partnershipInterests={a.partnershipInterests} demo />
        </section>

        {/* 6. DISCOVER MORE */}
        <section aria-labelledby="discover" className="flex flex-col gap-4">
          <h2 id="discover" className={`${label} text-muted-foreground`}>Discover More</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {demoRelated.map((r) => (
              <Link key={r.name} to="/athletes" className="group overflow-hidden rounded-2xl bg-card shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                <img src={r.image} alt="" loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform group-hover:scale-[1.02]" />
                <div className="p-4">
                  <p className={`${label} text-primary-strong`}>{r.sport}</p>
                  <p className="mt-1 font-display text-xl uppercase">{r.name}</p>
                  <p className="text-xs text-muted-foreground">{r.tagline}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="rounded-3xl bg-secondary p-6 text-center text-secondary-foreground md:p-10">
          <Sparkles className="mx-auto h-6 w-6 text-primary-on-navy" aria-hidden />
          <p className="serif mt-3 text-3xl italic md:text-4xl">Want a profile like this?</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Link to="/become-an-athlete" className={`${pill} bg-primary text-primary-foreground hover:bg-primary-strong`}>Get started <ArrowRight className="h-4 w-4" aria-hidden /></Link>
            <Link to="/athlete-business-hub-demo" className={`${pill} border border-secondary-foreground/40 hover:bg-secondary-foreground/10`}>See the athlete side</Link>
          </div>
        </section>
      </main>

      <Footer />

      <FollowAthleteDialog open={followOpen} onOpenChange={setFollowOpen} athleteId={a.id} athleteName={a.name}
        athleteSlug={a.slug} firstName={a.firstName} isFollowing={following} onSaved={async () => setFollowing(true)} demo />

      <Dialog open={supportOpen} onOpenChange={setSupportOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase">Back {a.firstName} directly</DialogTitle>
            <DialogDescription>
              On a live profile, this step hands fans off to the athlete's approved external support page. SIIS never processes the payment.
            </DialogDescription>
          </DialogHeader>
          <p className="rounded-xl bg-muted p-3 text-sm">Demo profile: no external page opens and no payment is taken.</p>
          <DialogFooter><Button onClick={() => setSupportOpen(false)}>Got it</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AthleteProfileDemo;
